import {Axios} from 'services/axios';


const cleanDefaultStructureForJson = (structure) => {
  if (!structure || typeof structure !== 'object') return structure;

  const cleaned = Array.isArray(structure) ? [...structure] : { ...structure };

  Object.entries(cleaned).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    // Remove File objects — they should never be in JSON
    if (value instanceof File) {
      delete cleaned[key];
      return;
    }

    // Handle image-like objects (device keys)
    if (typeof value === 'object' && !Array.isArray(value)) {
      const deviceKeys = ['mobile', 'tablet', 'desktop'];
      const isDeviceObject = Object.keys(value).some(k => deviceKeys.includes(k));

      if (isDeviceObject) {
        const cleanedDevice = {};
        Object.entries(value).forEach(([device, val]) => {
          if (val instanceof File) {
            // Skip — file is appended separately
          } else if (typeof val === 'string' && val !== '' && !val.startsWith('blob:')) {
            cleanedDevice[device] = val;
          }
        });
        cleaned[key] = cleanedDevice;
        return;
      }

      // Recurse into nested objects
      cleaned[key] = cleanDefaultStructureForJson(value);
      return;
    }

    // Handle arrays (gallery or repeater)
    if (Array.isArray(value)) {
      cleaned[key] = value
        .map(item => {
          if (item instanceof File) return null;
          if (item && typeof item === 'object') {
            return cleanDefaultStructureForJson(item);
          }
          return item;
        })
        .filter(item => {
          if (item === null) return false;
          // Remove empty device objects (all values were blob/File)
          if (typeof item === 'object' && !Array.isArray(item)) {
            const vals = Object.values(item);
            if (vals.length === 0) return false;
            // If it looks like a device object with no valid URLs, skip
            const deviceKeys = ['mobile', 'tablet', 'desktop'];
            const isDeviceObj = Object.keys(item).every(k => deviceKeys.includes(k));
            if (isDeviceObj && vals.every(v => !v || v === '')) return false;
          }
          return true;
        });
      return;
    }

    // String — remove blob URLs
    if (typeof value === 'string' && value.startsWith('blob:')) {
      cleaned[key] = '';
      return;
    }
  });

  return cleaned;
};

const appendFilesToFormData = (formData, imageFiles, galleryFiles, prefix = 'defaultContent') => {
  // Append image files: key format = defaultContent_image_{fieldKey}_{device}
  if (imageFiles && typeof imageFiles === 'object') {
    Object.entries(imageFiles).forEach(([fieldKey, deviceFiles]) => {
      if (!deviceFiles || typeof deviceFiles !== 'object') return;
      Object.entries(deviceFiles).forEach(([device, file]) => {
        if (file instanceof File) {
          const formFieldName = `${prefix}_image_${fieldKey}_${device}`;
          formData.append(formFieldName, file);
        }
      });
    });
  }

  // Append gallery files: key format = defaultContent_gallery_{fieldKey}_{device}_{index}
  if (galleryFiles && typeof galleryFiles === 'object') {
    Object.entries(galleryFiles).forEach(([fieldKey, deviceFiles]) => {
      if (!deviceFiles || typeof deviceFiles !== 'object') return;
      Object.entries(deviceFiles).forEach(([device, files]) => {
        if (Array.isArray(files)) {
          files.forEach((file, index) => {
            if (file instanceof File) {
              const formFieldName = `${prefix}_gallery_${fieldKey}_${device}_${index}`;
              formData.append(formFieldName, file);
            }
          });
        }
      });
    });
  }
};

const buildTemplateFormData = (templateData) => {
  const formData = new FormData();

  formData.append('name', templateData.name || '');
  formData.append('typeKey', templateData.typeKey || '');
  formData.append('description', templateData.description || '');
  formData.append('category', templateData.category || 'custom');
  formData.append('isSystem', templateData.isSystem ? 'true' : 'false');
  formData.append('isLocked', templateData.isLocked ? 'true' : 'false');
  formData.append('scope', templateData.scope || 'global');

  // Always send countryId — empty string means "All Countries" (null on server)
  formData.append('countryId', templateData.countryId || '');

  if (templateData.isActive !== undefined) {
    formData.append('isActive', templateData.isActive ? 'true' : 'false');
  }

  if (templateData.changeReason) {
    formData.append('changeReason', templateData.changeReason);
  }

  if (
    templateData.maxUsagePerPage !== undefined &&
    templateData.maxUsagePerPage !== null
  ) {
    formData.append('maxUsagePerPage', templateData.maxUsagePerPage);
  }

  if (templateData.scope === 'page-specific') {
    const pageIds = Array.isArray(templateData.allowedPageIds)
      ? templateData.allowedPageIds.filter(Boolean)
      : [];
    formData.append('allowedPageIds', JSON.stringify(pageIds));
  }

  if (templateData.fieldDefinitions) {
    const fieldDefinitionsWithOrder = templateData.fieldDefinitions.map(
      (field, index) => ({
        ...field,
        order: index,
      }),
    );
    formData.append(
      'fieldDefinitions',
      JSON.stringify(fieldDefinitionsWithOrder),
    );
  }

  // ============================================================
  // KEY CHANGE: Handle defaultStructure like SectionContentEditor
  // 1. Clean the JSON (remove blob URLs, File objects)
  // 2. Append cleaned JSON as 'defaultContent'
  // 3. Append actual files separately to FormData
  // ============================================================
  if (templateData.defaultStructure) {
    const cleanedContent = cleanDefaultStructureForJson(templateData.defaultStructure);
    formData.append('defaultContent', JSON.stringify(cleanedContent));
  }

  // Append image and gallery files separately
  appendFilesToFormData(
    formData,
    templateData.imageFiles || {},
    templateData.galleryFiles || {},
    'defaultContent',
  );

  if (templateData.settingsSchema) {
    formData.append(
      'settingsSchema',
      JSON.stringify(templateData.settingsSchema),
    );
  }

  if (templateData.thumbnailFile instanceof File) {
    formData.append('thumbnail', templateData.thumbnailFile);
  } else if (templateData.thumbnailUrl) {
    formData.append('thumbnailUrl', templateData.thumbnailUrl);
  }

  return formData;
};

export const sectionTemplateApi = {
  createTemplate: async templateData => {
    try {
      const formData = buildTemplateFormData(templateData);

      const response = await Axios.post('/section-templates', formData, {
        'Content-Type': 'multipart/form-data',
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error creating section template:', error);
      let errorMessage = 'Failed to create section template';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  updateTemplate: async (id, templateData) => {
    try {
      if (!id) {
        throw new Error('Template ID is required for update');
      }

      const formData = buildTemplateFormData(templateData);

      const response = await Axios.put(`section-templates/${id}`, formData, {
        'Content-Type': 'multipart/form-data',
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error updating section template:', error);
      let errorMessage = 'Failed to update section template';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  getAllTemplates: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        search = '',
        category = '',
        countryId = '',
        isActive = '',
        isSystem = '',
        scope = '',
        sortBy = 'createdAt',
        sortOrder = 'DESC',
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });

      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (countryId) params.append('countryId', countryId);
      if (isActive !== '') params.append('isActive', isActive);
      if (isSystem !== '') params.append('isSystem', isSystem);
      if (scope) params.append('scope', scope);

      const response = await Axios.fetch(`section-templates?${params}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      if (!responseData.templates || !Array.isArray(responseData.templates)) {
        console.warn('Templates data is not an array:', responseData);
        return {
          templates: [],
          pagination: {
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
            hasMore: false,
          },
        };
      }

      return responseData;
    } catch (error) {
      console.error('Error fetching templates:', error);
      let errorMessage = 'Failed to fetch templates';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      throw new Error(errorMessage);
    }
  },

  getTemplatesByCountry: async (countryId, options = {}) => {
    try {
      if (!countryId) {
        throw new Error('Country ID is required');
      }

      const {page = 1, limit = 20, category = '', search = ''} = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (category) params.append('category', category);
      if (search) params.append('search', search);

      const response = await Axios.fetch(
        `section-templates/by-country/${countryId}?${params}`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching templates by country:', error);

      let errorMessage = 'Failed to fetch templates by country';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  copyTemplateToCountry: async (id, targetCountryId, options = {}) => {
    try {
      if (!id || !targetCountryId) {
        throw new Error('Template ID and target country ID are required');
      }

      const {newName, newTypeKey} = options;

      const response = await Axios.post(
        `section-templates/${id}/copy-to-country`,
        {
          targetCountryId,
          newName,
          newTypeKey,
        },
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error copying template to country:', error);

      let errorMessage = 'Failed to copy template to country';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getTemplateById: async id => {
    try {
      if (!id) {
        throw new Error('Template ID is required');
      }

      const response = await Axios.fetch(`section-templates/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const templateData = response.data.data || response.data;

      if (!templateData.template) {
        throw new Error('Template not found');
      }

      return templateData;
    } catch (error) {
      console.error('Error fetching template:', error);

      let errorMessage = 'Failed to fetch template';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Template not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteTemplate: async (id, forceDelete = false) => {
    try {
      if (!id) {
        throw new Error('Template ID is required');
      }

      const params = new URLSearchParams();
      if (forceDelete) {
        params.append('forceDelete', 'true');
      }

      const url = params.toString()
        ? `section-templates/${id}?${params}`
        : `section-templates/${id}`;

      const response = await Axios.delete(url);

      return response.data;
    } catch (error) {
      console.error('Error deleting template:', error);

      let errorMessage = 'Failed to delete template';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getTemplateVersions: async (id, options = {}) => {
    try {
      if (!id) {
        throw new Error('Template ID is required');
      }

      const {page = 1, limit = 20} = options;
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      const response = await Axios.fetch(
        `section-templates/${id}/versions?${params}`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching template versions:', error);

      let errorMessage = 'Failed to fetch template versions';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getVersionDetails: async versionId => {
    try {
      if (!versionId) {
        throw new Error('Version ID is required');
      }

      const response = await Axios.fetch(
        `section-templates/versions/${versionId}`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching version details:', error);

      let errorMessage = 'Failed to fetch version details';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  restoreTemplateVersion: async (id, versionId, changeReason = '') => {
    try {
      if (!id || !versionId) {
        throw new Error('Template ID and Version ID are required');
      }

      const response = await Axios.post(
        `section-templates/${id}/restore/${versionId}`,
        {
          changeReason: changeReason || `Restored from version ${versionId}`,
        },
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error restoring template version:', error);

      let errorMessage = 'Failed to restore template version';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  validateTemplateStructure: async fieldDefinitions => {
    try {
      if (!Array.isArray(fieldDefinitions)) {
        throw new Error('Field definitions must be an array');
      }

      const response = await Axios.post('section-templates/validate', {
        fieldDefinitions,
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error validating template structure:', error);

      let errorMessage = 'Failed to validate template structure';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  duplicateTemplate: async (id, options = {}) => {
    try {
      if (!id) {
        throw new Error('Template ID is required');
      }

      const {newName, newTypeKey} = options;

      const response = await Axios.post(`section-templates/${id}/duplicate`, {
        newName,
        newTypeKey,
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error duplicating template:', error);

      let errorMessage = 'Failed to duplicate template';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getTemplateCategories: async (options = {}) => {
    try {
      const {countryId} = options;

      let url = 'section-templates/categories/list';
      if (countryId) {
        url += `?countryId=${countryId}`;
      }

      const response = await Axios.fetch(url);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const categoriesData = response.data.data || response.data || {};

      return categoriesData.categories || [];
    } catch (error) {
      console.error('Error fetching template categories:', error);

      let errorMessage = 'Failed to fetch template categories';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  bulkUpdateTemplateStatus: async (templateIds, isActive, countryId = null) => {
    try {
      if (!Array.isArray(templateIds) || templateIds.length === 0) {
        throw new Error('Template IDs array is required');
      }

      if (isActive === undefined) {
        throw new Error('isActive status is required');
      }

      const payload = {
        templateIds,
        isActive,
      };

      if (countryId) {
        payload.countryId = countryId;
      }

      const response = await Axios.patch(
        'section-templates/bulk/status',
        payload,
      );

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating templates status:', error);

      let errorMessage = 'Failed to update templates status';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getTemplateStats: async (countryId = null) => {
    try {
      let url = 'section-templates/stats/overview';
      if (countryId) {
        url += `?countryId=${countryId}`;
      }

      const response = await Axios.fetch(url);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching template stats:', error);

      let errorMessage = 'Failed to fetch template statistics';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  uploadGalleryImages: async files => {
    try {
      if (!Array.isArray(files) || files.length === 0) {
        throw new Error('Files array is required');
      }

      const formData = new FormData();
      files.forEach(file => {
        if (file instanceof File) {
          formData.append('galleryImages', file);
        }
      });

      const response = await Axios.post('upload/gallery', formData, {
        'Content-Type': 'multipart/form-data',
      });

      return response.data.data || response.data;
    } catch (error) {
      console.error('Error uploading gallery images:', error);

      let errorMessage = 'Failed to upload gallery images';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  transformTemplateDataForForm: template => {
    if (!template) return {};

    // defaultContent from backend — this is the raw JSON stored in TemplateDefaultContent
    const rawDefaultContent = template.defaultContent || {};

    return {
      id: template.id,
      name: template.name || '',
      typeKey: template.typeKey || '',
      description: template.description || '',
      category: template.category || 'custom',
      countryId: template.countryId || '',
      thumbnailUrl: template.thumbnailUrl || '',
      galleryUrls: template.galleryUrls || [],
      fieldDefinitions: template.fieldDefinitions || [],
      defaultStructure: rawDefaultContent,
      settingsSchema: template.settingsSchema || {
        padding: {top: 'medium', bottom: 'medium'},
        bgColor: '#ffffff',
        textAlign: 'left',
        containerWidth: 'full',
      },
      isSystem: template.isSystem || false,
      isLocked: template.isLocked || false,
      isActive: template.isActive !== undefined ? template.isActive : true,
      usageCount: template.usageCount || 0,
      createdAt: template.createdAt,
      updatedAt: template.updatedAt,
      country: template.country || null,

      maxUsagePerPage: template.maxUsagePerPage,
      allowedPageIds: template.allowedPageIds || [],
      scope: template.scope || 'global',
    };
  },

  prepareTemplateForApi: formData => {
    return {
      name: formData.name,
      typeKey: formData.typeKey,
      description: formData.description,
      category: formData.category,
      countryId: formData.countryId,
      thumbnailFile: formData.thumbnailFile,
      thumbnailUrl: formData.thumbnailUrl,
      galleryFiles: formData.galleryFiles,
      galleryUrls: formData.galleryUrls,
      fieldDefinitions: formData.fieldDefinitions,
      defaultStructure: formData.defaultStructure,
      settingsSchema: formData.settingsSchema,
      isSystem: formData.isSystem,
      isLocked: formData.isLocked,
      isActive: formData.isActive,
      changeReason: formData.changeReason,

      maxUsagePerPage: formData.maxUsagePerPage,
      allowedPageIds: formData.allowedPageIds,
      scope: formData.scope,
    };
  },
};
